import { Graphics } from '@inlet/react-pixi';
import type { EmailIntegration } from 'lib-shared-legacy-types';
import { useMemo } from 'react';

import ChatContactFormModal from 'components/powerChatFlow/containers/modals/contactform';
import type { OptionT } from 'components/powerFlowShared/components/types';
import { useFindQueue } from 'components/powerFlowShared/hooks/useFindQueue';
import { usePixiBadge } from 'components/powerFlowShared/hooks/usePixiBadge';
import { ContactFormModal as MessangerContactFormModal } from 'components/settings/components/sections/dixaMessenger/views/flow/components/nodeTypeContactForm/components/contactFormModal';
import { flowNodeColor, FlowType } from 'fable/flowEditor';
import type {
  ChatExtraData,
  MessengerExtraData,
} from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

export type Payload = {
  queue: OptionT;
  fromEmail?: EmailIntegration;
};

type AcceptableExtraData = MessengerExtraData | ChatExtraData;

const getContactFormModal = (type: FlowType) => {
  switch (type) {
    case FlowType.FLOW_TYPE_CHAT:
      return ChatContactFormModal;
    case FlowType.FLOW_TYPE_MESSENGER:
      return MessangerContactFormModal;
    default:
      return () => null;
  }
};

export const createContactFormNodeAttributes = <
  TExtraData extends AcceptableExtraData,
>(
  type: FlowType,
): GenericNodeAttributes<Payload, TExtraData> => ({
  iconName: 'chat-widget',
  label: 'Contact form',
  color: hexStringToNumber(flowNodeColor.green),
  translatePayload: () => [],
  updateConnection: (node) => node,
  NodeContent: ContactFormNode,
  ActionModal: getContactFormModal(type),
});

export function ContactFormNode<TExtraData extends AcceptableExtraData>({
  node,
  context,
}: GenericNodeProps<Payload, TExtraData>): JSX.Element {
  const { extraData } = context;
  const { payload } = node;

  const queue = useFindQueue(payload.queue, extraData);

  const drawArchivedBadge = usePixiBadge({
    text: 'Archived',
    variant: 'error',
    offset: { x: 0, y: 28 },
  });

  return (
    <TextContent>
      {useMemo(
        () => (
          <>
            <TextEllipsis>
              <Text weight="normal" text="Queue" />
              <Text
                weight="semibold"
                text={queue?.name ?? payload.queue.label ?? 'No queue selected'}
              />
            </TextEllipsis>

            {queue?.is_archived && <Graphics draw={drawArchivedBadge} />}
          </>
        ),
        [drawArchivedBadge, payload.queue, queue],
      )}
    </TextContent>
  );
}

export const ContactFormNodeChatAttributes =
  createContactFormNodeAttributes<ChatExtraData>(FlowType.FLOW_TYPE_CHAT);
export const ContactFormNodeMessengerAttributes =
  createContactFormNodeAttributes<MessengerExtraData>(
    FlowType.FLOW_TYPE_MESSENGER,
  );
